import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { AskAnswer, AskBlock, AskTreeNode, AskTurn } from '@shared/views';
import { api } from '../api/client.js';
import { navigate, parseHash } from '../router.js';

/**
 * สถานะระหว่างรอคำตอบ
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมเป็นโครงร่างของคำตอบ ไม่ใช่วงหมุน
 * ────────────────────────────────────────────────────────────────
 *
 * วงหมุนบอกได้อย่างเดียวว่า "ยังไม่เสร็จ" ซึ่งบนคำขอที่ใช้เวลา 3-10 วินาที
 * ทำให้ผู้ใช้เดาไม่ออกว่าอีกนานไหม และหน้าจอกระโดดตอนคำตอบมาแทนที่
 *
 * โครงร่างจาง ๆ ที่มีรูปร่างเท่าคำตอบจริง (บรรทัดคำบรรยาย + แถวตาราง) แก้ทั้งสองอย่าง —
 * ผู้ใช้เห็นล่วงหน้าว่ากำลังจะได้อะไร และตอนคำตอบมาแทนที่ ความสูงเปลี่ยนน้อยกว่ามาก
 *
 * ⚠ ข้อความบอกขั้นตอนไล่ไปตามเวลาจริงของวงจร (เลือกวิธีค้น → คำนวณ → เรียบเรียง)
 * ไม่ใช่ข้อความสุ่มเพื่อความสวยงาม ถ้าวันหนึ่งวงจรเปลี่ยน ต้องแก้ตรงนี้ให้ตรงด้วย
 * ข้อความที่บอกขั้นตอนผิดแย่กว่าไม่บอกอะไรเลย
 */
const THINKING_STEPS = [
  'กำลังอ่านคำถาม…',
  'กำลังเลือกวิธีค้นจากฐานข้อมูล…',
  'กำลังคำนวณและเรียบเรียงคำตอบ…',
];

function Thinking() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    // เดินหน้าอย่างเดียว ไม่วนกลับ — วนกลับไปข้อแรกจะอ่านว่าระบบเริ่มใหม่
    const t = window.setInterval(
      () => setStep((v) => Math.min(v + 1, THINKING_STEPS.length - 1)),
      1600,
    );
    return () => window.clearInterval(t);
  }, []);

  return (
    <div className="kkthink" role="status" aria-live="polite">
      <div className="kkthink-hd">
        <span className="kkdots" aria-hidden="true">
          <i /><i /><i />
        </span>
        <span className="kkthink-t">{THINKING_STEPS[step]}</span>
      </div>
      {/* โครงร่างของคำตอบ — aria-hidden เพราะเป็นภาพประกอบล้วน
          ข้อความสถานะข้างบนคือสิ่งที่โปรแกรมอ่านหน้าจอต้องอ่าน */}
      <div className="kkskel" aria-hidden="true">
        <span className="l w80" />
        <span className="l w60" />
        <span className="l w95" />
        <span className="l w70" />
      </div>
    </div>
  );
}

/** หนึ่งรอบของบทสนทนา — คำถามขึ้นทันที คำตอบตามมาทีหลัง */
interface Turn {
  question: string;
  answer: AskAnswer | null;
  error: string | null;
}

/**
 * หน้าถามคำถามด้วยภาษาไทย
 *
 * ════════════════════════════════════════════════════════════════
 *  สิ่งที่หน้านี้ตั้งใจ "ไม่" เป็น
 * ════════════════════════════════════════════════════════════════
 *
 * ไม่ใช่หน้าแชท ตั้งใจให้เป็นหน้า "ถามหนึ่งข้อ ได้คำตอบหนึ่งชุด" มากกว่า
 * เพราะสิ่งที่ผู้ใช้ระบบนี้ต้องการคือตัวเลขที่เอาไปใส่บันทึกเสนอได้
 * ไม่ใช่บทสนทนา การทำเป็นแชทจะชวนให้ถามต่อเรื่อย ๆ แล้วคำตอบที่ดีที่สุด
 * จะเลื่อนหายขึ้นไปข้างบนจอ
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมตัวเลขไม่ได้อยู่ในประโยค
 * ────────────────────────────────────────────────────────────────
 *
 * คำอธิบายที่ AI เขียนอยู่ข้างบน ส่วนตัวเลขทั้งหมดอยู่ในการ์ดกับตารางข้างล่าง
 * ซึ่งเซิร์ฟเวอร์คำนวณเองทั้งหมด (ดู server/src/services/ask/tools.ts)
 * แยกกันแบบนี้เพื่อให้ผู้ใช้เห็นด้วยตาว่าอะไรคือข้อเท็จจริงจากฐานข้อมูล
 * และอะไรคือคำพูดของเครื่อง — สองอย่างนี้ปนกันในย่อหน้าเดียวเมื่อไร
 * ก็ไม่มีใครแยกออกอีกต่อไป
 */
export function Ask() {
  const status = useQuery({ queryKey: ['ask-status'], queryFn: api.askStatus });
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  /**
   * บทสนทนาที่แสดงบนจอ — ต่อกันไปเรื่อย ไม่ใช่แทนที่ของเดิม
   *
   * ⚠ สิ่งที่แสดง กับสิ่งที่ส่งกลับไปให้โมเดล เป็นคนละก้อนโดยตั้งใจ
   *
   * ก้อนนี้ยาวได้เท่าที่ผู้ใช้คุย เพราะเป็นบันทึกให้คนอ่านย้อน
   * ส่วน `history` ด้านล่างถูกตัดเหลือสามรอบล่าสุดและเก็บแค่ข้อความ ไม่เก็บตาราง
   * เพราะทุกอย่างในนั้นถูกส่งออกไปนอกระบบทุกครั้งที่พิมพ์คำถามถัดไป
   *
   * ถ้าวันหนึ่งจะรวมสองก้อนนี้เป็นก้อนเดียวเพื่อความง่าย จะกลายเป็นการส่งรายชื่อ
   * และตัวเลขทั้งบทสนทนาออกไปให้ผู้ให้บริการโมเดลทุกครั้งที่ถามเพิ่มหนึ่งคำถาม
   */
  const [turns, setTurns] = useState<Turn[]>([]);

  /**
   * บทสนทนาก่อนหน้า — เก็บไว้ที่หน้าจอ ไม่ใช่ที่เซิร์ฟเวอร์
   *
   * เก็บแค่คู่ (คำถาม, คำบรรยาย) ไม่เก็บตารางหรือรายชื่อ เพราะสิ่งที่ส่งกลับเข้าไป
   * มีหน้าที่เดียวคือทำให้ "แล้วปีถัดไปล่ะ" รู้ว่ากำลังพูดถึงเรื่องอะไร
   * ตัวเลขต้องเรียกเครื่องมือใหม่อยู่ดี การส่งของเก่ากลับไปจึงไม่ได้ช่วยอะไร
   * แต่ทำให้ข้อมูลบุคคลถูกส่งออกไปซ้ำทุกครั้งที่พิมพ์คำถามถัดไป
   */
  const [history, setHistory] = useState<AskTurn[]>([]);

  const run = useMutation({
    mutationFn: (q: string) => api.ask(q, history),
    onSuccess: (a, q) => {
      setTurns((t) => t.map((x) => (x.question === q && !x.answer ? { ...x, answer: a } : x)));
      /*
       * เก็บเข้าบริบทเฉพาะคำตอบที่ AI เขียนเอง
       * ประโยคแม่แบบไม่ได้บอกเนื้อหาอะไรที่ใช้อ้างต่อได้ และคำถามกลับก็ยังไม่มีคำตอบ
       * ส่งสองอย่างนี้กลับไปมีแต่จะทำให้รอบถัดไปสับสน
       */
      if (!a.narrationFromTemplate && !a.clarify) {
        /* ⚠ จำนวนรอบที่เก็บมาจากเซิร์ฟเวอร์ (ask/status) ไม่ใช่เลขที่เขียนไว้ตรงนี้
           เดิมหน้าจอตัดที่ 3 ส่วนเซิร์ฟเวอร์ก็ตัดที่ 3 เป็นเลขเดียวกันที่เขียนไว้สองที่
           วันที่ใครแก้ฝั่งเดียว อีกฝั่งจะเงียบ ๆ ตัดทิ้งโดยไม่มีอะไรบอก */
        const keep = status.data?.historyLimit ?? 3;
        setHistory((h) => [...h, { question: a.question, answer: a.narration }].slice(-keep));
      }
    },
    onError: (e, q) => {
      const msg = e instanceof Error ? e.message : 'ถามไม่สำเร็จ';
      setTurns((t) => t.map((x) => (x.question === q && !x.answer ? { ...x, error: msg } : x)));
    },
  });

  function submit(q: string) {
    const text = q.trim();
    if (text.length < 2 || run.isPending) return;
    // ขึ้นคำถามบนจอทันที ไม่ต้องรอคำตอบ — ผู้ใช้จะได้เห็นว่าระบบรับคำถามไปแล้ว
    setTurns((t) => [...t, { question: text, answer: null, error: null }]);
    setDraft('');
    run.mutate(text);
  }

  function reset() {
    setHistory([]);
    setTurns([]);
    setDraft('');
    inputRef.current?.focus();
  }

  /*
   * เลื่อนลงล่างสุดเมื่อบทสนทนายาวขึ้น
   *
   * ⚠ เลื่อนที่กล่องบทสนทนา ไม่ใช่ scrollIntoView ของการ์ดคำตอบ
   * การ์ดบางใบสูงกว่าจอ ถ้าเลื่อนไปหาหัวการ์ด ผู้ใช้จะเห็นแต่คำถามของตัวเอง
   * ส่วนคำตอบอยู่ใต้ขอบจอ แล้วดูเหมือนกดแล้วไม่มีอะไรเกิดขึ้น
   */
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  if (status.isPending) return <p className="state">กำลังตรวจสอบว่าเปิดใช้การถามด้วย AI ไว้หรือไม่…</p>;

  if (!status.data?.enabled) {
    return (
      /*
       * ⚠ ห้ามใช้ .asksec / .wrap / .askhead ตรงนี้
       *
       * สามคลาสนั้นเป็นโครงของ "หน้าเต็ม" — .wrap ใส่ระยะขอบของทั้งหน้า
       * ส่วน .askhead เป็น flex ที่ตั้ง justify-content: space-between ไว้สำหรับ
       * หัวเรื่องซ้าย-ปุ่มขวา พอเอามาใช้ในนี้ซึ่งมีแค่สองบรรทัด ป้ายเล็กจะไปชิดซ้าย
       * แล้วหัวเรื่องถูกดันไปอีกฝั่งจนดูเหมือนจัดกึ่งกลางผิด ๆ
       *
       * ตอนนี้คู่คิดถูกฝังในหน้าต่างของตัวเอง (KookitModal) ไม่ใช่หน้าเต็มอีกแล้ว
       * สถานะนี้จึงต้องมีโครงของตัวเองที่ไม่พึ่งโครงของหน้า
       */
      <div className="askoff-box">
        <p className="askeyebrow">ถาม-ตอบด้วย AI</p>
        <h2>ยังไม่ได้เปิดใช้งาน</h2>
        <div className="askoff">
          <p>
            ฟีเจอร์นี้ต้องตั้งค่ากุญแจของผู้ให้บริการโมเดลที่เซิร์ฟเวอร์ก่อน
            ตั้งค่าที่ตัวแปร <code>KAMLANG_GEMINI_KEY</code> แล้วรีสตาร์ทเซิร์ฟเวอร์
          </p>
          {/* ⚠ เดิมเขียนว่า "มีหน้าจอของตัวเองอยู่แล้วในเมนูด้านซ้าย" ซึ่งไม่จริงมาตั้งแต่
              ยุบเมนูเจ็ดอันทิ้งแล้วเหลือหน้าเดียว (ดู pages/Console.tsx)
              ผู้ใช้ที่อ่านแล้วไปหาเมนูด้านซ้ายจะไม่เจออะไรเลย และจะสรุปว่าระบบพัง */}
          <p className="asknote">
            ทุกตัวเลขที่หน้านี้ตอบได้ อ่านได้จากผังและแผงข้อมูลที่อยู่หลังหน้าต่างนี้อยู่แล้ว
            ระบบจึงใช้งานได้ครบทุกอย่างแม้ไม่เปิดฟีเจอร์นี้
          </p>
        </div>
      </div>
    );
  }

  return (
    /*
     * โครงแบบแชท — บทสนทนาเลื่อนได้ ช่องพิมพ์ตรึงอยู่ล่างสุด
     *
     * ⚠ ความสูงต้องคุมด้วย flex ทั้งสาย (.kk → .kk-scroll) และ .kk-scroll ต้องมี min-height: 0
     * ขาดข้อใดข้อหนึ่ง กล่องบทสนทนาจะยืดตามเนื้อหาแล้วดันช่องพิมพ์หลุดออกนอกจอ
     * ซึ่งบนหน้าต่างที่สูงคงที่แปลว่าพิมพ์คำถามถัดไปไม่ได้เลย
     */
    <section className="kk">
      <div className="kk-scroll" ref={scrollRef}>
        {turns.length === 0 && (
          <div className="kk-intro">
            <p className="askhint">
              ถามเป็นภาษาพูดได้เลย — AI ทำหน้าที่แปลคำถามเป็นเงื่อนไขการค้น
              ตัวเลขทุกตัวคำนวณจากฐานข้อมูลโดยตรง ไม่ได้มาจากการเดาของ AI
            </p>
            <div className="asksamples">
              <p className="asksamples-h">ลองถามแบบนี้</p>
              <ul>
                {status.data.samples.map((sq) => (
                  <li key={sq}>
                    <button type="button" onClick={() => submit(sq)}>{sq}</button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {turns.map((t, i) => (
          <div className="kk-turn" key={`${i}-${t.question}`}>
            <p className="kk-you">{t.question}</p>
            {t.answer ? (
              <AnswerCard answer={t.answer} onAsk={submit} />
            ) : t.error ? (
              <p className="state error">{t.error}</p>
            ) : (
              <Thinking />
            )}
          </div>
        ))}
      </div>

      <div className="kk-compose">
        {history.length > 0 && (
          <p className="askthread">
            ส่งบริบทจาก {history.length} คำถามล่าสุดไปด้วย — พิมพ์อ้างถึงสิ่งที่คุยไปแล้วได้
            <button type="button" onClick={reset}>เริ่มหัวข้อใหม่</button>
          </p>
        )}

        <form
          className="askbox"
          onSubmit={(e) => {
            e.preventDefault();
            submit(draft);
          }}
        >
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            /*
             * Enter ส่ง / Shift+Enter ขึ้นบรรทัดใหม่ — ตรงกับที่คนคุ้นจากแอปแชท
             * ใช้ textarea ไม่ใช่ input เพราะคำถามที่เจาะจงพอจะได้คำตอบดี มักยาวเกินหนึ่งบรรทัด
             */
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit(draft);
              }
            }}
            rows={2}
            maxLength={400}
            placeholder="เช่น อีก 5 ปีข้างหน้าหน่วยไหนจะเกษียณเยอะที่สุด"
            aria-label="คำถาม"
          />
          <button type="submit" className="askgo" disabled={run.isPending || draft.trim().length < 2}>
            {run.isPending ? 'กำลังค้น…' : 'ถาม'}
          </button>
        </form>
      </div>
    </section>
  );
}

/**
 * การ์ดคำตอบหนึ่งใบ
 *
 * ⚠ ไม่วาดคำถามซ้ำ และไม่เลื่อนจอเอง
 *
 * เดิมการ์ดนี้ขึ้นทีละใบและวาดคำถามไว้บนหัวการ์ดเอง พร้อม scrollIntoView ของตัวเอง
 * พอเปลี่ยนเป็นบทสนทนาต่อกัน ทั้งสองอย่างกลายเป็นปัญหา — คำถามจะซ้ำกับฟองคำถาม
 * ที่อยู่เหนือการ์ดอยู่แล้ว ส่วน scrollIntoView จะเลื่อนไปหา "หัวการ์ด" ซึ่งบนการ์ด
 * ที่สูงกว่าจอ แปลว่าผู้ใช้เห็นแต่คำถามตัวเอง ส่วนคำตอบอยู่ใต้ขอบจอ
 * การเลื่อนจึงย้ายไปทำที่กล่องบทสนทนาแทน (ดู useEffect ใน Ask)
 */
function AnswerCard({ answer, onAsk }: { answer: AskAnswer; onAsk: (q: string) => void }) {

  /*
   * ⚠ ถามกลับแล้วต้อง "แทนที่" คำตอบ ไม่ใช่ขึ้นคู่กัน
   *
   * ตอนที่ระบบถามกลับ แปลว่ามันยังไม่ได้ตอบอะไรเลย ฝั่งเซิร์ฟเวอร์จึงส่ง blocks
   * มาเป็นอาเรย์ว่างโดยตั้งใจ ถ้าที่นี่ยังวาดโครงคำตอบตามปกติ ผู้ใช้จะเห็น
   * หัวข้อ "ตัวเลขนี้มาจาก" กับกล่องคำบรรยายว่างเปล่าอยู่ใต้คำถามกลับ
   * ซึ่งอ่านได้ว่าระบบตอบแล้วแต่ตอบไม่ครบ ทั้งที่มันยังไม่ได้เริ่มตอบ
   */
  if (answer.clarify) {
    const c = answer.clarify;
    return (
      <article className="askans is-clarify">
        {answer.warnings.map((w) => (
          <p className="askwarn" key={w}>{w}</p>
        ))}
        <div className="askclarify">
          <p className="ac-msg">{c.message}</p>
          <ul className="ac-list">
            {c.choices.map((ch) => (
              <li key={ch.question}>
                <button type="button" onClick={() => onAsk(ch.question)}>
                  <b>{ch.label}</b>
                  {/* คำถามเต็มต้องเห็นก่อนกด ไม่ใช่เห็นหลังกด
                      ปุ่มที่ไม่บอกว่าจะถามอะไรจริง ๆ คือปุ่มที่ผู้ใช้ต้องกดเพื่อลองดู */}
                  <span>{ch.question}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="ac-note">
            หรือพิมพ์คำถามใหม่ในช่องด้านบนได้เลย
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="askans">

      {/*
        คำเตือนที่ต้องอ่านก่อนเชื่อคำตอบเท่านั้นที่ขึ้นตรงนี้
        ส่วนข้อสังเกตเรื่องคุณภาพข้อมูลถูกพับไปไว้ท้ายการ์ด — ดูเหตุผลใน domain/views.ts
      */}
      {answer.warnings.map((w) => (
        <p className="askwarn" key={w}>
          {w}
        </p>
      ))}

      <div className={`asksay${answer.narrationFromTemplate ? ' is-template' : ''}`}>
        {answer.narration.split('\n').filter(Boolean).map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>

      {answer.view && VIEW_LABEL[answer.view] && (
        <p className="askview">{VIEW_LABEL[answer.view]}</p>
      )}

      <Blocks blocks={answer.blocks} />

      {answer.supporting.length > 0 && (
        <details className="askmore">
          <summary>
            ข้อมูลประกอบที่ค้นมาระหว่างทาง ({countTables(answer.supporting)})
          </summary>
          <Blocks blocks={answer.supporting} />
        </details>
      )}

      {answer.notes.length > 0 && (
        <details className="askmore is-notes">
          <summary>ข้อสังเกตเรื่องคุณภาพข้อมูล {answer.notes.length} ข้อ</summary>
          <ul className="asknotes">
            {answer.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </details>
      )}

      {answer.sources.length > 0 && (
        <footer className="asksrc">
          <span className="asksrc-h">ตัวเลขนี้มาจาก</span>
          <ul>
            {answer.sources.map((s) => (
              <li key={`${s.tool}-${JSON.stringify(s.args)}`}>
                {s.href ? (
                  <button type="button" onClick={() => navigate(parseHash(s.href!))}>
                    {s.label}
                    <span aria-hidden="true"> ›</span>
                  </button>
                ) : (
                  <span>{s.label}</span>
                )}
              </li>
            ))}
          </ul>
        </footer>
      )}

      {/*
        คำถามต่อเนื่อง — วางท้ายการ์ด ใต้ที่มาของตัวเลข

        ⚠ ต้องอยู่ "หลัง" คำตอบเสมอ ห้ามเอาไปไว้บน
        มันคือทางเลือกหลังอ่านจบ ไม่ใช่เมนู ถ้าขึ้นก่อนคำตอบ ผู้ใช้จะกดก่อนอ่าน
        แล้ววนถามไปเรื่อยโดยไม่เคยได้อ่านสิ่งที่ตัวเองถามมาตั้งแต่แรก
      */}
      {answer.followUps.length > 0 && (
        <div className="asknext">
          <p className="asknext-h">ถามต่อได้</p>
          <ul>
            {answer.followUps.map((f) => (
              <li key={f}>
                <button type="button" onClick={() => onAsk(f)}>{f}</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

/**
 * หนึ่งกิ่งของผัง
 *
 * ใช้ ul/li ซ้อนกันจริง ๆ ไม่ใช่ div ที่เยื้องด้วย padding
 * เพราะโปรแกรมอ่านหน้าจอบอก "รายการซ้อนรายการ" ให้เองได้จากโครงสร้างนี้
 * ส่วนเส้นโยงวาดด้วย ::before ของ CSS ซึ่งเป็นการตกแต่งล้วน ๆ ไม่กระทบความหมาย
 */
function TreeBranch({ node }: { node: AskTreeNode }) {
  return (
    <li>
      <div className="asktree-n">
        {node.href ? (
          <button type="button" className="asktree-l" onClick={() => navigate(parseHash(node.href!))}>
            {node.label}
          </button>
        ) : (
          <span className="asktree-l">{node.label}</span>
        )}
        {node.meta && <em>{node.meta}</em>}
      </div>
      {node.children.length > 0 && (
        <ul>
          {node.children.map((c) => (
            <TreeBranch node={c} key={c.label} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** นับว่ามีตาราง/กราฟกี่ชิ้น เพื่อบอกในหัวข้อที่พับไว้ว่ากางแล้วจะเจออะไร */
function countTables(blocks: AskBlock[]): string {
  const n = blocks.filter((b) => b.kind !== 'stat').length;
  return n > 0 ? `${n} ตาราง/กราฟ` : `${blocks.length} รายการ`;
}

/** ป้ายบอกว่าคำตอบนี้ถูกจัดรูปมา ไม่ใช่รูปดิบของเครื่องมือ */
const VIEW_LABEL: Record<string, string> = {
  text: 'ตอบเป็นข้อความ',
  stat: 'เน้นตัวเลขสรุป',
  table: 'แสดงเป็นตาราง',
  bars: 'แสดงเป็นกราฟเทียบ',
  tree: 'แสดงเป็นผังลำดับชั้น',
};

function Blocks({ blocks }: { blocks: AskBlock[] }) {
  if (blocks.length === 0) return null;
  return (
    <div className="askblocks">
      {groupStats(blocks).map((group, i) =>
        Array.isArray(group) ? (
          <div className="askstats" key={`s${i}`}>
            {group.map((s) => (
              <div className={`askstat${s.tone === 'warn' ? ' is-warn' : ''}`} key={s.label}>
                <em>{s.label}</em>
                <b className="tnum">{s.value.toLocaleString('th-TH')}</b>
                <small>{s.unit}</small>
              </div>
            ))}
          </div>
        ) : (
          <BlockView block={group} key={`b${i}`} />
        ),
      )}
    </div>
  );
}

/**
 * รวม stat ที่อยู่ติดกันให้เป็นแถวเดียว
 *
 * เครื่องมือแต่ละตัวส่ง stat มาทีละก้อน ถ้าวาดตามลำดับตรง ๆ จะได้การ์ดใบใหญ่เรียงลงมา
 * สี่ใบเต็มความกว้าง ซึ่งกินพื้นที่จนตารางที่เป็นเนื้อหาจริงถูกดันตกจอไปหมด
 */
type StatBlock = Extract<AskBlock, { kind: 'stat' }>;

function groupStats(blocks: AskBlock[]): (StatBlock[] | AskBlock)[] {
  const out: (StatBlock[] | AskBlock)[] = [];
  let run: StatBlock[] = [];

  const flush = () => {
    if (run.length > 0) {
      out.push(run);
      run = [];
    }
  };

  for (const b of blocks) {
    if (b.kind === 'stat') run.push(b);
    else {
      flush();
      out.push(b);
    }
  }
  flush();
  return out;
}

function BlockView({ block }: { block: AskBlock }) {
  if (block.kind === 'table') {
    return (
      <div className="askblock">
        <h4>{block.title}</h4>
        <div className="asktable-wrap">
          <table className="asktable">
            <thead>
              <tr>
                {block.columns.map((c) => (
                  <th key={c.key} className={c.numeric ? 'is-num' : undefined}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {block.columns.map((c) => {
                    const v = row[c.key];
                    return (
                      <td key={c.key} className={c.numeric ? 'is-num tnum' : undefined}>
                        {v == null ? '—' : typeof v === 'number' ? v.toLocaleString('th-TH') : v}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {block.note && <p className="asknote">{block.note}</p>}
      </div>
    );
  }

  if (block.kind === 'tree') {
    return (
      <div className="askblock">
        <h4>{block.title}</h4>
        <ul className="asktree">
          <TreeBranch node={block.root} />
        </ul>
        {block.note && <p className="asknote">{block.note}</p>}
      </div>
    );
  }

  if (block.kind === 'bars') {
    // สเกลเทียบกับค่าสูงสุดในชุดนี้ ไม่ใช่ 100 — ไม่งั้นชุดที่ค่าน้อยทั้งชุดจะดูแบนเท่ากันหมด
    const max = Math.max(...block.items.map((i) => i.value), 1);
    return (
      <div className="askblock">
        <h4>{block.title}</h4>
        <ul className="askbars">
          {block.items.map((it) => (
            <li key={it.label}>
              <span className="askbar-l">{it.label}</span>
              <span className="askbar-t">
                <span
                  className={`askbar-f${it.tone === 'warn' ? ' is-warn' : ''}`}
                  // ค่า 0 ต้องไม่มีแท่ง — แท่งจิ๋ว ๆ ที่ยาวเท่ากันหมดทำให้ 0 กับ 1 ดูเหมือนกัน
                  style={{ width: it.value <= 0 ? 0 : `${Math.max(2, (it.value / max) * 100)}%` }}
                />
              </span>
              <b className="askbar-v tnum">
                {it.value.toLocaleString('th-TH')}
                <small> {block.unit}</small>
              </b>
            </li>
          ))}
        </ul>
        {block.note && <p className="asknote">{block.note}</p>}
      </div>
    );
  }

  return null;
}
